import React, { useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../Auth/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Plus, MapPin, User, LogOut, Tag, Hash, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { TagSelector } from '../ui/TagSelector';
import apiService from '../../services/api';

function Header() {
  const { state, actions } = useApp();
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const isMainPage = location.pathname === '/';

  // Load tags data on component mount
  useEffect(() => {
    const loadTagsData = async () => {
      try {
        const [categories, allTagsFromSpots] = await Promise.all([
          apiService.getTagCategories(),
          apiService.getAllTagsFromSpots()
        ]);
        actions.setTagCategories(categories);
        actions.setAllTags(allTagsFromSpots);
      } catch (error) {
        console.error('Error loading tags data:', error);
        try {
          const tags = await apiService.getTags();
          actions.setAllTags(tags);
        } catch (fallbackError) {
          console.error('Error loading fallback tags:', fallbackError);
        }
      }
    };

    if (isMainPage && state.tagCategories.length === 0) {
      loadTagsData();
    }
  }, [isMainPage, state.tagCategories.length, actions]);

  // Enhanced search functionality with tags
  const performSearch = async () => {
    try {
      const query = state.searchQuery.trim();
      const tags = state.selectedTags;
      
      // Use the new advanced search endpoint
      if (query || tags.length > 0) {
        const result = await apiService.searchSpots(query, tags);
        actions.setFilteredSpots(result.results || []);
        console.log(`Search: "${query}" with tags [${tags.join(', ')}] - Found ${result.count} spots`);
      } else {
        // No search criteria - show all spots
        actions.setFilteredSpots(state.spots);
      }
    } catch (error) {
      console.error('Error searching spots:', error);
      // Fallback to local filtering if API fails
      const query = state.searchQuery.toLowerCase();
      const filtered = state.spots.filter(spot => {
        const matchesQuery = !query || 
          spot.title?.toLowerCase().includes(query) ||
          spot.description?.toLowerCase().includes(query) ||
          spot.tags?.some(tag => tag.toLowerCase().includes(query)) ||
          spot.author?.toLowerCase().includes(query);
        
        const matchesTags = state.selectedTags.length === 0 ||
          state.selectedTags.every(tag => spot.tags?.includes(tag));
        
        return matchesQuery && matchesTags;
      });
      actions.setFilteredSpots(filtered);
    }
  };

  // Debounced search - triggers automatically after user stops typing
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (isMainPage) {
        performSearch();
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(debounceTimer);
  }, [state.searchQuery, state.selectedTags, isMainPage]);

  const handleSearchChange = (e) => {
    actions.setSearchQuery(e.target.value);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      performSearch();
    }
  };

  const handleSearchValidation = () => {
    performSearch();
  };

  // Clear search
  const handleClearSearch = () => {
    actions.setSearchQuery('');
    actions.setSelectedTags([]);
    actions.setFilteredSpots(state.spots);
  };

  const handleTagsChange = (newTags) => {
    actions.setSelectedTags(newTags);
    // Search will be triggered automatically by the useEffect
  };

  const handleTimeSlotsChange = (newTimeSlots) => {
    actions.setSelectedTimeSlots(newTimeSlots);
    setTimeout(() => {
      performSearch();
    }, 100);
  };

  const handleAddSpotClick = () => {
    if (!isAuthenticated) {
      // Rediriger vers la page de connexion
      navigate('/login', { state: { from: location, action: 'addSpot' } });
    } else {
      actions.setShowAddSpot(true);
    }
  };

  const handleLoginClick = () => {
    // Rediriger vers la nouvelle page de connexion
    navigate('/login');
  };

  const handleLogoutClick = () => {
    logout();
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const getProfilePhotoUrl = () => {
    if (user?.profile_photo) {
      return user.profile_photo.startsWith('http') 
        ? user.profile_photo 
        : `${process.env.REACT_APP_BACKEND_URL}${user.profile_photo}`;
    }
    return null;
  };

  const getUserInitials = () => {
    if (user?.username) {
      return user.username.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  return (
    <header className="bg-white border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        {/* Logo and Title */}
        <div className="flex items-center space-x-4">
          <div 
            className="flex items-center cursor-pointer"
            onClick={() => navigate('/')}
          >
            <img src="/branding/logo-96.png" alt="Sun-Spot" className="h-10 w-auto" />
          </div>
          
          {isMainPage && (
            <div className="text-sm text-gray-600">
              {state.spots.length} spots découverts
            </div>
          )}
        </div>

        {/* Search Bar and Tags - only on main page */}
        {isMainPage && (
          <div className="flex-1 max-w-2xl mx-8">
            <div className="flex items-center space-x-3">
              {/* Tag Selector */}
              <TagSelector
                selectedTags={state.selectedTags}
                selectedTimeSlots={state.selectedTimeSlots}
                onTagsChange={handleTagsChange}
                onTimeSlotsChange={handleTimeSlotsChange}
                tagCategories={state.tagCategories}
                allTags={state.allTags}
                searchQuery={state.searchQuery}
                onSearchChange={actions.setSearchQuery}
                showSelectedTags={false}
              />
              
              {/* Selected Tags and Time Slots */}
              {(state.selectedTags.length > 0 || state.selectedTimeSlots.length > 0) && (
                <div className="flex items-center space-x-2 max-w-80">
                  {/* Time Slots */}
                  {state.selectedTimeSlots.length > 0 && (
                    <div className="flex items-center space-x-1">
                      {state.selectedTimeSlots.length === 1 ? (
                        (() => {
                          const TIME_SLOTS = [
                            { id: 'night', name: 'Nuit', color: '#1e293b' },
                            { id: 'pre-sunrise', name: 'Avant lever', color: '#3730a3' },
                            { id: 'sunrise', name: 'Lever', color: '#ea580c' },
                            { id: 'morning', name: 'Matinée', color: '#fbbf24' },
                            { id: 'midday', name: 'Midi', color: '#f59e0b' },
                            { id: 'afternoon', name: 'Après-midi', color: '#d97706' },
                            { id: 'pre-sunset', name: 'Début coucher', color: '#c2410c' },
                            { id: 'sunset', name: 'Coucher', color: '#dc2626' },
                            { id: 'evening', name: 'Soirée', color: '#7c3aed' }
                          ];
                          const slot = TIME_SLOTS.find(s => s.id === state.selectedTimeSlots[0]);
                          return (
                            <div
                              className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ 
                                backgroundColor: `${slot?.color}20`, 
                                color: slot?.color,
                                border: `1px solid ${slot?.color}40`
                              }}
                            >
                              <span>🕒</span>
                              <span className="max-w-16 truncate">{slot?.name}</span>
                              <button
                                onClick={() => handleTimeSlotsChange([])}
                                className="hover:bg-white hover:bg-opacity-50 rounded-full p-0.5 transition-colors"
                              >
                                <X size={10} />
                              </button>
                            </div>
                          );
                        })()
                      ) : (
                        <div className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                          <span>🕒</span>
                          <span>{state.selectedTimeSlots.length} périodes</span>
                          <button
                            onClick={() => handleTimeSlotsChange([])}
                            className="hover:bg-white hover:bg-opacity-50 rounded-full p-0.5 transition-colors"
                          >
                            <X size={10} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Tags */}
                  {state.selectedTags.length > 0 && (
                    <div className="flex items-center space-x-1">
                      {state.selectedTags.length === 1 ? (
                        (() => {
                          const tagSlug = state.selectedTags[0];
                          const tag = state.allTags.find(t => t.slug === tagSlug);
                          const category = state.tagCategories.find(c => c.id === tag?.category_id);
                          return (
                            <div
                              className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ 
                                backgroundColor: `${category?.color}20`, 
                                color: category?.color,
                                border: `1px solid ${category?.color}40`
                              }}
                            >
                              <Hash size={10} />
                              <span className="max-w-16 truncate">{tag?.name || tagSlug}</span>
                              <button
                                onClick={() => handleTagsChange([])}
                                className="hover:bg-white hover:bg-opacity-50 rounded-full p-0.5 transition-colors"
                              >
                                <X size={10} />
                              </button>
                            </div>
                          );
                        })()
                      ) : (
                        <div className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                          <Hash size={10} />
                          <span>{state.selectedTags.length} tags</span>
                          <button
                            onClick={() => handleTagsChange([])}
                            className="hover:bg-white hover:bg-opacity-50 rounded-full p-0.5 transition-colors"
                          >
                            <X size={10} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Search Input */}
              <div className="relative flex-1">
                <Input
                  type="text"
                  placeholder="Rechercher par titre, description, tags..."
                  value={state.searchQuery}
                  onChange={handleSearchChange}
                  onKeyPress={handleSearchKeyPress}
                  className={state.searchQuery ? "pr-20" : "pr-10"}
                  data-testid="search-input"
                />
                {state.searchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-9 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-100"
                    onClick={handleClearSearch}
                    data-testid="clear-search-btn"
                  >
                    <X size={14} className="text-gray-400" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-blue-50"
                  onClick={handleSearchValidation}
                  data-testid="search-btn"
                >
                  <Search size={16} className={state.searchQuery ? "text-blue-500" : "text-gray-400"} />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
          <Button
            onClick={handleAddSpotClick}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700"
          >
            <Plus size={16} />
            <span>Ajouter un spot</span>
          </Button>

          {/* User Menu */}
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center space-x-2 h-10 px-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={getProfilePhotoUrl()} alt={user?.username} />
                    <AvatarFallback className="text-xs">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-gray-700 font-medium">{user?.username}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={handleProfileClick}>
                  <User className="h-4 w-4 mr-2" />
                  Mon profil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogoutClick}>
                  <LogOut className="h-4 w-4 mr-2" />
                  Se déconnecter
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="ghost"
              onClick={handleLoginClick}
              className="flex items-center space-x-2"
            >
              <User size={16} />
              <span>Connexion</span>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
